// src/supabase.js
// ─────────────────────────────────────────────────────────────
// Supabase client — imported by App.jsx once connected.
// Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY
// in .env.local (copy from .env.example).
// ─────────────────────────────────────────────────────────────
import { createClient } from '@supabase/supabase-js';

const supabaseUrl  = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey  = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  console.warn(
    'LoanFlow: Supabase env vars not set. ' +
    'Copy .env.example to .env.local and fill in your project URL and anon key. ' +
    'App will fall back to localStorage until connected.'
  );
}

// Every request gets an abort timeout. Without one, a single stalled fetch
// (flaky wifi, laptop wake, proxy hiccup) hangs for the browser's own limit —
// 5-10 MINUTES in Chrome — and the app sits empty the whole time because
// nothing retries. Failing fast lets the existing error paths keep prior
// state and the 30-second poll / lazy-load retry recover. Storage transfers
// (file uploads/downloads) get a longer leash than REST/auth calls.
// Lender Library originals (bucket 'lender-library', up to 200 MB each on the
// Pro plan) go up and come down in ONE request, so their object transfers get
// far longer still: 200 MB on a 2 Mbit/s home upload is ~14 minutes. Only the
// object upload/download paths match — signing, listing and deleting in that
// bucket are quick calls and keep the normal storage limit.
const REST_TIMEOUT_MS = 30000;
const STORAGE_TIMEOUT_MS = 120000;
const LIBRARY_FILE_TIMEOUT_MS = 30 * 60000;
const LIBRARY_FILE_RE = /\/storage\/v1\/object\/(?:authenticated\/)?lender-library\//;
function timeoutFor(url) {
  if (LIBRARY_FILE_RE.test(url)) return LIBRARY_FILE_TIMEOUT_MS;
  return url.indexOf("/storage/v1/") !== -1 ? STORAGE_TIMEOUT_MS : REST_TIMEOUT_MS;
}
function fetchWithTimeout(input, init) {
  const url = typeof input === "string" ? input : (input && (input.url || input.href)) || "";
  const ms = timeoutFor(url);
  const ctl = new AbortController();
  const timer = setTimeout(function () { ctl.abort(new DOMException("LoanFlow: request timed out after " + ms + "ms", "TimeoutError")); }, ms);
  // Honor any abort signal supabase-js itself passes along.
  const outer = init && init.signal;
  if (outer) {
    if (outer.aborted) ctl.abort(outer.reason);
    else outer.addEventListener("abort", function () { ctl.abort(outer.reason); });
  }
  return fetch(input, Object.assign({}, init, { signal: ctl.signal }))
    .finally(function () { clearTimeout(timer); });
}

export const supabase = (supabaseUrl && supabaseKey)
  ? createClient(supabaseUrl, supabaseKey, {
      auth: {
        // Persist the Supabase auth session in localStorage so a page reload
        // doesn't kick the admin back to the login screen every time.
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
      },
      global: { fetch: fetchWithTimeout },
    })
  : null;

export const isSupabaseReady = () => !!supabase;

// Role identifier — must match the Supabase Auth user you create in the
// dashboard. Change here (or via env var) if you pick a different email,
// then also update the auth_is_admin() helper in schema.sql.
export const ADMIN_EMAIL = process.env.REACT_APP_SUPABASE_ADMIN_EMAIL
  || "admin@reversebyreece.local";

// Second staff login — Max signs in at /max with this Supabase Auth email.
// Both staff are user==="admin" inside the app, but Max is LIMITED for now
// (Reece, 10/1): Calculator, his own expenses, the Lender Library chatbot and
// a short Settings page — see src/access.js. The database enforces it:
// auth_is_admin() is Reece only, auth_is_staff() is either login
// (supabase/2026-10-01-max-limited-access.sql); expenses stay per person (RLS
// on expenses.owner). Change the address here AND in auth_user_key() (SQL)
// AND in api/anthropic-proxy.js / api/borrower/* together.
export const MAX_EMAIL = process.env.REACT_APP_SUPABASE_MAX_EMAIL
  || "max@reversebyreece.local";

// Who is signed in. The key is what gets stamped on owned records
// (loan.owner, lead "owner:<key>" tag, expenses.owner, scenarios.owner).
// What each key may use is src/access.js (staffAccess).
export const STAFF = {
  reece: { key: "reece", name: "Reece", short: "R" },
  max:   { key: "max",   name: "Max",   short: "M" },
};

// Supabase Auth email → staff key ("reece" | "max"), or null for anyone else.
export function staffFromEmail(email) {
  const e = String(email || "").trim().toLowerCase();
  if (!e) return null;
  if (e === String(ADMIN_EMAIL).toLowerCase()) return "reece";
  if (e === String(MAX_EMAIL).toLowerCase()) return "max";
  return null;
}
